import type { CSSProperties } from "react";
import type { FeedMessage } from "@/types";
import {
  CLEVERENCE_AUTHOR,
  CLEVERENCE_AVATAR_URL,
  isCleverenceAuthor,
} from "@/constants/cleverence";
import { authorHue, authorInitials, formatMessageTime } from "@/utils/format";
import {
  formatMessageText,
  messageTextPlain,
} from "@/utils/formatMessageText";
import { messageIdKey } from "@/utils/messageId";

function chatLabel(source?: string): string | null {
  if (!source?.startsWith("archive:")) return null;
  const name = source.slice("archive:".length).trim();
  return name || null;
}

function TrashIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" strokeLinecap="round" />
      <path d="M10 11v6M14 11v6" strokeLinecap="round" />
    </svg>
  );
}

interface Props {
  message: FeedMessage;
  replyTo?: FeedMessage;
  highlighted?: boolean;
  /** Админка «Планирование»: корзина всегда видна */
  adminMode?: boolean;
  deleteBusy?: boolean;
  onGoToMessage?: (messageId: string) => void;
  onDeleteMessage?: () => void;
}

export function MessageBubble({
  message,
  replyTo,
  highlighted,
  adminMode = false,
  deleteBusy = false,
  onGoToMessage,
  onDeleteMessage,
}: Props) {
  const showReply = Boolean(replyTo && onGoToMessage);
  const replyTargetId = showReply ? messageIdKey(replyTo!.id) : null;
  const fromChat = chatLabel(message.source);
  const isCleverence = isCleverenceAuthor(message.author);
  const isService =
    !message.is_live &&
    !isCleverence &&
    (message.author_kind === "service" || message.author_kind === "bot");
  const isTyping = message.is_typing;
  const hue = authorHue(message.author);
  const initials = authorInitials(message.author);
  const displayAuthor = isCleverence ? CLEVERENCE_AUTHOR : message.author;
  const canDelete = adminMode && !isTyping && onDeleteMessage != null;
  const useMarkdown =
    isCleverence || message.author_kind === "bot" || isService;

  return (
    <article
      id={`msg-${message.id}`}
      className={`message-bubble ${highlighted ? "highlighted" : ""} ${isService ? "service" : ""} ${isCleverence ? "cleverence" : ""} ${message.is_live ? "live" : ""} ${isTyping ? "typing" : ""}`}
    >
      {isCleverence ? (
        <div className="message-avatar message-avatar--cleverence" aria-hidden>
          <img
            src={CLEVERENCE_AVATAR_URL}
            alt=""
            width={36}
            height={36}
            loading="lazy"
          />
        </div>
      ) : (
        <div
          className="message-avatar"
          style={{ "--avatar-hue": String(hue) } as CSSProperties}
          aria-hidden
        >
          {initials}
        </div>
      )}

      <div className="message-body">
        <header className="message-meta">
          <span className="message-meta-main">
            <span className="message-author">{displayAuthor}</span>
          {fromChat && !message.is_live ? (
            <span className="message-chat" title="Чат-источник">
              {fromChat}
            </span>
          ) : null}
          {message.is_live && isCleverence && !isTyping && (
            <span className="message-badge message-badge--ai">AI</span>
          )}
          {message.is_live && !isCleverence && !isTyping && (
            <span className="message-badge">сейчас</span>
          )}
            <time className="message-time" dateTime={message.date}>
              {formatMessageTime(message.date)}
            </time>
          </span>
          {canDelete ? (
            <button
              type="button"
              className="message-delete message-delete--visible"
              title="Убрать из очереди"
              aria-label="Убрать сообщение из очереди"
              disabled={deleteBusy}
              onClick={() => onDeleteMessage!()}
            >
              <TrashIcon />
            </button>
          ) : null}
        </header>

        {showReply ? (
          <button
            type="button"
            className="message-reply"
            title={replyTo?.text ?? "Перейти к сообщению"}
            onClick={() => onGoToMessage!(replyTargetId!)}
          >
            <span className="message-reply-author">
              {isCleverenceAuthor(replyTo!.author) ?
                CLEVERENCE_AUTHOR
              : replyTo!.author}
            </span>
            <span className="message-reply-text">
              {(() => {
                const plain = messageTextPlain(replyTo!.text);
                return plain.length > 140 ? `${plain.slice(0, 140)}…` : plain;
              })()}
            </span>
          </button>
        ) : null}

        <div className="message-text">
          {isTyping ? (
            <span className="typing-dots">
              Думаю<span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          ) : (
            formatMessageText(message.text, { markdown: useMarkdown })
          )}
        </div>
      </div>
    </article>
  );
}
